import { Alert, Card, Descriptions, Skeleton, Space, Tag, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { ContactRevealControl } from './ContactRevealControl'
import { isMaskedPhone, type ContactOwnerType } from './contactApi'
import { useOwnerContact } from './useOwnerContact'

interface ContactCardProps {
  ownerType: ContactOwnerType
  ownerId: string
  /**
   * SHU EKRANDA allaqachon OCHIQ turgan telefon raqami (bo'lsa).
   *
   * ⚠️ Bu «qiymatni ko'rsat» degani EMAS — karta uni chizmaydi. U faqat
   * bitta savolga javob beradi: «bu ekranda raqam ochiq turibdimi?».
   * Turgan bo'lsa reestrdagi NIQOBLANGAN nusxa raqam sifatida
   * chizilmaydi — sabab {@link PhoneValue} da.
   */
  openPhone?: string | null
}

/**
 * Kontakt ma'lumotlari — FAQAT O'QISH uchun.
 *
 * <h2>⚠️ NEGA BU KARTA KERAK BO'LDI</h2>
 * Viloyat, tuman, manzil va elektron pochta faqat TAHRIRLASH formasida
 * ko'rinardi. Ya'ni «abonent qayerda yashaydi?» degan savolga javob
 * olish uchun operator o'zgartirish oynasini ochishga majbur edi — bu
 * ham qulaysiz, ham xavfli: ochilgan formada tasodifiy tahrir qilish
 * oson, va `contact.write` yo'q odam javobni umuman ololmasdi.
 *
 * <h2>⚠️ TELEFON — YAGONA NIQOBLANGAN MAYDON</h2>
 * Server o'qish yo'lida FAQAT ikkita telefonni niqoblaydi
 * (`ContactEntry#masked()`: `Phones.mask(phone)`, `Phones.mask(phoneAlt)`).
 * Manzil, e-pochta, viloyat va tuman TO'LIQ keladi va bu ataylab —
 * manzilni bilish o'z-o'zidan aloqa kanali bermaydi.
 *
 * Shuning uchun niqoblangan raqam bu yerda NUSXA OLINADIGAN matn
 * SIFATIDA ko'rsatilmaydi: u `code` uslubida va yoniga «niqoblangan»
 * yorlig'i bilan chiziladi. Aks holda operator `+9989****567` ni haqiqiy
 * raqam deb nusxa olib, qo'ng'iroq qilolmay qolardi va sababini
 * bilmasdi.
 *
 * <h2>⚠️ EKRANDA RAQAM OCHIQ BO'LSA — NIQOB CHIZILMAYDI</h2>
 * Abonent kartochkasida IKKI raqam bor va ular ikki BOSHQA ustundan
 * keladi: `subscriber.phone` (SMS va chek aynan shunga ketadi —
 * `FundTarget`) va `platform.contact.phone` (reestrdagi nusxa). Ularni
 * hech narsa bir-biriga tenglashtirmaydi: yaratish formasida ikkita
 * ALOHIDA maydon bor (`CreateSubscriberModal`), ya'ni ular farq qilishi
 * MUMKIN — jonli bazada ular hatto shakl bilan farq qiladi
 * (`+998901090600` va `901090600`).
 *
 * ⚠️ LEKIN birinchisi ATAYLAB niqoblanmaydi (`Subscriber#phone`
 * javadoc'i: «Chegara — ko'rinish doirasi, niqob emas») va u ekranda
 * ochiq turadi. O'shanda ikkinchisining niqobi HECH NARSANI himoya
 * qilmaydi: operator to'liq raqamni yonidagi kartadan o'qib turibdi.
 * Ya'ni bitta ekranda bitta raqam ham ochiq, ham niqoblangan ko'rinardi
 * — va `contact.reveal` bilan audit yozuvi shu bilan chetlab
 * o'tilgandek bo'lardi.
 *
 * ⚠️ NIMA SOLISHTIRILMAYDI: karta ikki raqamning BIR XILLIGINI
 * tekshirmaydi va tekshira ham olmaydi — niqobdan faqat 5 ta boshi va
 * 3 ta oxiri ko'rinadi (`Phones#mask`), ya'ni har qanday solishtiruv
 * TAXMIN bo'lardi va yolg'on «bir xil» xulosasi boshqa raqamni jimgina
 * yashirardi. Shuning uchun shart BOSHQA: «bu ekranda raqam ochiq
 * turibdimi». Turgan bo'lsa niqob RAQAM SIFATIDA chizilmaydi, o'rniga
 * neytral belgi va sabab qoladi — reestrdagi qiymatni o'qishning yagona
 * yo'li esa `Ochish` bo'lib qoladi, ya'ni audit TIKLANADI.
 *
 * ⚠️ `phoneAlt` ga bu QO'LLANMAYDI: qo'shimcha raqam ekranda boshqa
 * hech qayerda ko'rinmaydi, ya'ni uning niqobi haqiqiy ma'lumot tashiydi.
 *
 * ⚠️ OCHISH AVTOMATIK EMAS. `POST /api/contacts/{id}/reveal` majburiy
 * sabab talab qiladi va auditga yozadi. Sahifa ochilishida chaqirilsa,
 * «kim to'liq raqamni ko'rdi» jurnali har ochilishdan hosil bo'lgan
 * shovqinga to'lardi va o'z ma'nosini yo'qotardi.
 *
 * <h2>⚠️ «KONTAKT YO'Q» va «RUXSAT YO'Q» — IKKI BOSHQA HOLAT</h2>
 * 404 (`fetchContactByOwner` uni `null` ga aylantiradi) — yozuv yo'q,
 * uni to'ldirish kerak. 403 — yozuv BOR bo'lishi mumkin, lekin
 * ko'rsatilmayapti. Ikkalasini «kontakt yo'q» deb ko'rsatish operatorni
 * bo'sh formani to'ldirishga yuborardi va u yerda yana rad javobiga
 * urilardi (aynan shu nuqson sherik panelida topilgan edi).
 */
export function ContactCard({ ownerType, ownerId, openPhone = null }: ContactCardProps) {
  const { t } = useTranslation()
  const state = useOwnerContact(ownerType, ownerId, true)
  /* Bo'sh satr ham «yo'q» — `subscriber.phone` bo'sh bo'lsa ekranda
     ochiq raqam turmaydi va niqob o'z ma'nosini saqlaydi */
  const phoneOnScreen = openPhone !== null && openPhone.trim() !== ''

  const body = () => {
    if (!state.canRead) {
      return (
        <Alert
          type="info"
          showIcon
          message={t('contact.view.noPermissionTitle')}
          description={t('contact.view.noPermissionHint')}
        />
      )
    }

    if (state.loading) return <Skeleton active paragraph={{ rows: 5 }} />

    if (state.forbidden) {
      return (
        <Alert
          type="warning"
          showIcon
          message={t('contact.view.forbiddenTitle')}
          description={t('contact.view.forbiddenHint')}
        />
      )
    }

    if (state.error !== null) {
      return (
        <Alert type="error" showIcon message={t('contact.view.errorTitle')} description={state.error} />
      )
    }

    const contact = state.contact
    if (contact === null) {
      return (
        <Alert
          type="info"
          showIcon
          message={t('contact.view.missingTitle')}
          description={t('contact.view.missingHint')}
        />
      )
    }

    const hasMasked = isMaskedPhone(contact.phone) || isMaskedPhone(contact.phoneAlt)

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={t('contact.fullName')}>
            <PlainValue value={contact.fullName} />
          </Descriptions.Item>
          <Descriptions.Item label={t('contact.phone')}>
            <PhoneValue
              stored={contact.phone}
              revealed={state.revealed?.phone ?? null}
              openOnScreen={phoneOnScreen}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('contact.phoneAlt')}>
            <PhoneValue stored={contact.phoneAlt} revealed={state.revealed?.phoneAlt ?? null} />
          </Descriptions.Item>
          <Descriptions.Item label={t('contact.email')}>
            {/* Niqoblanmaydi — to'liq keladi, ya'ni nusxa olsa bo'ladi */}
            <PlainValue value={contact.email} copyable />
          </Descriptions.Item>
          {/* ⚠️ `regionName`/`districtName` SERVERDAN tayyor keladi.
              Reyestrdan qidirish qo'shimcha so'rov bo'lardi va reyestr
              sahifalanganda nofaol viloyat umuman topilmasdi. */}
          <Descriptions.Item label={t('geo.region')}>
            <PlainValue value={contact.regionName ?? contact.region} />
          </Descriptions.Item>
          <Descriptions.Item label={t('geo.district')}>
            <PlainValue value={contact.districtName ?? contact.district} />
          </Descriptions.Item>
          <Descriptions.Item label={t('contact.address')}>
            <PlainValue value={contact.address} />
          </Descriptions.Item>
          <Descriptions.Item label={t('contact.note')}>
            <PlainValue value={contact.note} />
          </Descriptions.Item>
        </Descriptions>

        {hasMasked && state.revealed === null ? (
          <Alert
            type="info"
            showIcon
            message={t('contact.view.maskedNote')}
            description={
              state.canReveal ? (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <span>{t('contact.view.maskedReveal')}</span>
                  <ContactRevealControl
                    onReveal={state.reveal}
                    revealing={state.revealing}
                    revealError={state.revealError}
                  />
                </Space>
              ) : (
                t('contact.view.maskedNoReveal')
              )
            }
          />
        ) : null}
      </Space>
    )
  }

  return <Card title={t('contact.section')}>{body()}</Card>
}

/**
 * Niqoblanmaydigan maydon.
 *
 * ⚠️ Bo'sh qiymat «—» EMAS: chiziqcha «bu maydon shu abonentga tegishli
 * emas» degan ma'noni beradi, holbuki bu yerda u «hali to'ldirilmagan» —
 * ya'ni operator tuzatishi mumkin bo'lgan holat.
 */
function PlainValue({ value, copyable = false }: { value: string | null; copyable?: boolean }) {
  const { t } = useTranslation()
  if (value === null || value.trim() === '') {
    return <Typography.Text type="secondary">{t('contact.view.empty')}</Typography.Text>
  }
  return <Typography.Text copyable={copyable}>{value}</Typography.Text>
}

/**
 * Telefon — niqob KO'RINIB TURSIN.
 *
 * ⚠️ Niqoblangan qiymat NUSXA OLINMAYDI. `copyable` qo'yilsa panel
 * `+9989****567` ni bufer orqali tashqariga chiqarardi: SMS ro'yxatiga
 * yoki hisobotga tushgan niqob u yerda haqiqiy raqamdan farq qilmasdi.
 *
 * @param openOnScreen shu ekranda egasining raqami ALLAQACHON ochiq
 *                     turibdi — o'shanda niqob RAQAM sifatida umuman
 *                     chizilmaydi (sinf javadoc'idagi sabab). Ochilgan
 *                     qiymat esa avvalgidek ko'rsatiladi: u operator
 *                     sabab yozib, audit orqali OLGAN natija.
 */
function PhoneValue({
  stored,
  revealed,
  openOnScreen = false,
}: {
  stored: string | null
  revealed: string | null
  openOnScreen?: boolean
}) {
  const { t } = useTranslation()

  if (revealed !== null && revealed !== '') {
    return (
      <Space size={8} wrap>
        <Typography.Text copyable>{revealed}</Typography.Text>
        <Tag color="success">{t('contact.view.revealedTag')}</Tag>
      </Space>
    )
  }

  if (stored === null || stored.trim() === '') {
    return <Typography.Text type="secondary">{t('contact.view.empty')}</Typography.Text>
  }

  /* ⚠️ NIQOB O'RNIGA IZOH — sonlar CHIZILMAYDI. Sabab sinf
     javadoc'ida: yonidagi kartada shu abonentning raqami ochiq turibdi
     va niqob u yerda faqat bitta raqamni ikki ko'rinishda ko'rsatardi. */
  if (openOnScreen && isMaskedPhone(stored)) {
    return (
      <Tooltip title={t('contact.view.onCardHint')}>
        <Typography.Text type="secondary">{t('contact.view.onCardTag')}</Typography.Text>
      </Tooltip>
    )
  }

  if (isMaskedPhone(stored)) {
    return (
      <Space size={8} wrap>
        <Typography.Text code>{stored}</Typography.Text>
        <Tag color="warning">{t('contact.view.maskedTag')}</Tag>
      </Space>
    )
  }

  return <Typography.Text copyable>{stored}</Typography.Text>
}
